import { useState, useEffect, useMemo, PropsWithChildren } from 'react';
import { useNavigate } from 'react-router';
import { DismissableErrorMessage, DismissableErrorMessageProps, Headline, Paragraph } from '../../ui';
import style from './style.module.scss';
import { navigateToStep, ApplicationPaths } from '../ApplicationFormsHelpers';
import { useApplicationIdContext } from '../../components/ApplicationContext';
import { trackSectionView } from '../../lib/gtm';
import { useTranslation } from 'react-i18next';
import { SignCommonComponent, useApplicationSignContext } from '@n2-common/components/Sign/SignCommon';
import { type CommonStatusPageProps } from '@n2-common/components/ApplicationStatusPage';
import { SubmitProgressBox } from '@n2-common/ui/SubmitProgress';
import { type SubmitState } from '@n2-common/components/MultiStageOnePageWizard/types';
import { NavigateLink } from '@n2-common/ui/Button';
import { TFunction } from 'i18next';
import { type NoteInfo } from '@n2-common/lib/useFootnotes';
import { type DocumentInfo, SingleUploadBox } from '@n2-common/components/UploadDocuments';
import { O1DocumentationContextProvider, useDocumentationContext } from '@n2-common/components/UploadDocuments';
import icons from '@n2-common/icons/icons.module.scss';

export function UploadDocumentsPage({ navigateToCorrectStepFunc }: CommonStatusPageProps): JSX.Element {
    return (
        <SignCommonComponent navigateToCorrectStepFunc={navigateToCorrectStepFunc}>
            <O1DocumentationContextProvider>
                <PageContent>
                    <UploadDocumentsComponent />
                </PageContent>
            </O1DocumentationContextProvider>
        </SignCommonComponent>
    );
}

function PageContent({ children }: PropsWithChildren) {
    const { t } = useTranslation('common');
    const navigate = useNavigate();

    const { applicantId, applicationId, isMainApplicant } = useApplicationSignContext();
    return (
        <>
            <NavigateLink
                text={` < ${t<string>('Navigation.Back')} `}
                onClick={() => navigateToStep(ApplicationPaths.sign, applicationId, navigate, applicantId)}
            />
            <div className={style.wrapper}>
                <Headline>
                    {t<string>('Sign.Documents.Headline')} {isMainApplicant === false ? `(${t<string>('CoApplicant')})` : ''}
                </Headline>
                <Paragraph>{t<string>('Sign.Documents.SubHeadline')}</Paragraph>
                {children}
            </div>
        </>
    );
}

export function UploadDocumentsComponent(): JSX.Element {
    const { t } = useTranslation('common');
    const footnoteSelector = useMemo(() => createFootnoteSelector(t), [t]);
    const { requirements, createUploadDocumentHandler, deleteDocument, trustedUpload } = useDocumentationContext();
    const [trustedStatus, setTrustedStatus] = useState<SubmitState>('NotSent');
    const { country, productType } = useApplicationIdContext();
    const [error, setError] = useState<DismissableErrorMessageProps['error']>();

    useEffect(() => {
        document.title = t<string>('Sign.Documents.Headline');
    }, [t]);

    useEffect(() => {
        trackSectionView(productType, country, '4.3', 'Upload documents');
    }, [country, productType]);

    const uploadBoxes = useMemo(() => {
        return requirements.map((requirement, i) => {
            const uploadDocumentHandler = createUploadDocumentHandler(requirement.documentType);
            const trustedUploaHandler = async () => {
                setTrustedStatus('Submitted');
                try {
                    await trustedUpload();
                } catch (err) {
                    setError(err as Error);
                    setTrustedStatus('Error');
                }
            };

            return (
                <SingleUploadBox
                    requirement={requirement}
                    key={i}
                    uploadDocument={uploadDocumentHandler}
                    deleteDocument={deleteDocument}
                    trustedUpload={trustedUploaHandler}
                    footnoteSelector={footnoteSelector}
                />
            );
        });
    }, [requirements, createUploadDocumentHandler, deleteDocument, footnoteSelector, trustedUpload]);

    return (
        <>
            <DismissableErrorMessage error={error} onDismiss={() => setError(undefined)} />
            {uploadBoxes}
            <SubmitProgressBox
                state={trustedStatus}
                submitText={t<string>('Sign.Documents.Trusted.SubmitText')}
                processingText={t<string>('Sign.Documents.Trusted.ProcessingText')}
            ></SubmitProgressBox>
        </>
    );
}

function createFootnoteSelector(t: TFunction) {
    const memo: Record<string, NoteInfo> = {};
    let errcount = 0;
    return function selector(info: DocumentInfo): NoteInfo {
        if (info.status in memo) {
            return memo[info.status];
        }
        let x = null;
        if (info.trusted) {
            x = { symbol: '🟡', description: t<string>('Sign.Documents.Trusted.ItemDescription') };
        } else {
            switch (info.status) {
                case 'P.New':
                    x = { className: icons.iconCircle, description: t<string>('Sign.Documents.ReceivedDescription') };
                    break;
                case 'A.Ok':
                    x = { className: icons.iconChecked, description: t<string>('Sign.Documents.Accepted') };
                    break;
                default:
                    if (info.error) {
                        x = { className: icons.iconDanger, id: `${++errcount}`, description: info.error };
                    } else {
                        x = null as never;
                    }
                    break;
            }
        }
        memo[info.status as keyof typeof memo] = x;
        return x as NoteInfo;
    };
}
